import React from 'react'
import { View, ScrollView, RefreshControl } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import SiteHeader from '../SiteHeader'
import ManifestationsHomeSection from '../ManifestationsHomeSection'
import Tabs from '@components/tabs/Tabs'
import Button from '@components/button/Button'
import IncidentsHotlineHomeSection from './IncidentsHotlineHomeSection'
import DraftsButton from '@components/button/DraftsButton'

import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'

import useReducer from '@store/useReducer'
import useBreakpoint from '@utils/useBreakpoints'
import useI18n from '@i18n/useI18n'
import useConnectivity from '@utils/useConnectivity'
import useNavigation from '@layout/useNavigation'
import useDrafts from '@utils/useDrafts'

import { store, refresh } from '@utils/useSiteChangeHandler'

type Props = {
  onBack?: () => void
}

const MAIN_VERTICAL_PADDING = 24

const HotlineHomeScreen = ({ onBack }: Props) => {
  const i18n = useI18n()
  const [online, loading] = useConnectivity()
  const navigation = useNavigation()

  const site = useReducer(SiteStore.store, (s) => s.site)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const draftsCount = useDrafts()[0]

  const status = useReducer(store, (s) => s.status)

  const [hasScrolled, setHasScrolled] = React.useState(false)
  const [incidentsOffset, setIncidentsOffset] = React.useState(0)
  const [manifestationsOffset, setManifestationsOffset] = React.useState(0)

  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const scroll = React.useRef<ScrollView>(null)

  const tabsData: TabOption[] = [
    { label: i18n.t('screens.site.incidents'), value: 'incident' },
    { label: i18n.t('screens.site.manifestations'), value: 'manifestations' },
  ]

  const startButton = {
    label: i18n.t('screens.site.startIncident'),
    onClick: () => navigation.push('/incident/start'),
  }

  const onMapPressed = () => navigation.push('/plan')

  const headerButtons: HeaderButton[] = React.useMemo(() => {
    if (!site) {
      return []
    }

    const buttons: HeaderButton[] = /*siteInfo.mapId*/ true
      ? [
          {
            label: i18n.t('screens.site.plan'),
            onClick: onMapPressed,
            color: 'light',
          },
        ]
      : []

    return [...buttons, startButton]
  }, [site, i18n])

  const onTabItemSelected = (opt: TabOption) => {
    if (scroll.current) {
      const offset = opt.value === 'incident' ? incidentsOffset : manifestationsOffset

      scroll.current.scrollTo({
        y: offset - MAIN_VERTICAL_PADDING,
        animated: true,
      })
    }
  }

  const refreshOnPull =
    online && site && !loading ? (
      <RefreshControl refreshing={status === 'loading'} onRefresh={() => refresh(site.id, scopes)} />
    ) : undefined

  const renderBottomButton = isPhone
    ? () => (
        <BottomButton width={width}>
          <Button status="active" label={startButton.label} onClick={startButton.onClick} />
        </BottomButton>
      )
    : undefined

  return (
    <Root
      loading={status === 'loading'}
      onLeft={onBack}
      header={site ? i18n.t(`screens.site.sites${site?.codeLieu}`) : i18n.t('screens.site.title')}
      renderFixedBottom={renderBottomButton}
      fixed
      renderHeader={
        isPhone ? () => <SiteHeader hasScrolled={hasScrolled} onBack={onBack} onMapPressed={onMapPressed} /> : undefined
      }>
      <ScrollView
        ref={scroll}
        scrollIndicatorInsets={{ right: 1 }}
        keyboardShouldPersistTaps="handled"
        onScroll={(event) => setHasScrolled(event.nativeEvent.contentOffset.y > 0)}
        scrollEventThrottle={16}
        refreshControl={refreshOnPull}>
        {!isPhone && (
          <Header
            siteIcon={site?.codeLieu as SiteIconName}
            title={i18n.t(`screens.site.sites.${site?.codeLieu}`)}
            buttons={headerButtons}
          />
        )}
        <MainContainer>
          {draftsCount > 0 && (
            <AlertButtonContainer>
              <DraftsButton count={draftsCount} onClick={() => navigation.push('/drafts')} />
            </AlertButtonContainer>
          )}

          {isPhone && (
            <TabsContainer>
              <Tabs equalTabs={false} data={tabsData} onItemSelected={onTabItemSelected} value={tabsData[0]} />
            </TabsContainer>
          )}

          <View onLayout={(e) => setIncidentsOffset(e.nativeEvent.layout.y)}>
            <IncidentsHotlineHomeSection />
          </View>

          <View onLayout={(e) => setManifestationsOffset(e.nativeEvent.layout.y)}>
            <ManifestationsHomeSection />
          </View>
        </MainContainer>
      </ScrollView>
    </Root>
  )
}

export default HotlineHomeScreen

const MainContainer = styled(View)`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
`

const BottomButton = styled(View)<{ width: number }>`
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background};
  padding: 16px;
`

const TabsContainer = styled(View)`
  margin-bottom: 16px;
`

const AlertButtonContainer = styled(View)`
  margin-bottom: 32px;
`
